import { NotificationDto, NotificationType } from '../models/notification';

/**
 * Where a notification leads when it is clicked (notifications page and topbar bell).
 *
 * `routeFor(n, roles)` returns an in-app URL for `router.navigateByUrl`, or null when the notification
 * has nowhere useful to go (it is then only marked read). The target depends on who is looking:
 * a student opens their own page, a parent the child's view, staff the management or grading page.
 *
 * `relatedEntityId` carries the id the sender stored: exam / quiz / assignment id, course or learning
 * path id, certificate id, lesson-session id, thread id, … (see the services that send each type).
 *
 * Pure and dependency-free so the layout bell can import it as well:
 * ```ts
 * const url = routeFor(n, this.auth.getUserRoles());
 * if (url) this.router.navigateByUrl(url);
 * ```
 */
export function routeFor(n: NotificationDto, roles: string[] | null | undefined): string | null {
  const r = new Set(roles ?? []);
  const isAdmin = r.has('Administrator') || r.has('TenantAdmin');
  const isStaff = isAdmin || r.has('Teacher');
  const isParent = !isStaff && r.has('Parent');
  const isStudent = !isStaff && !isParent && r.has('Student');

  const id = (n.relatedEntityId ?? '').trim() || null;
  const q = (path: string, params: Record<string, string | null | undefined>): string => {
    const entries = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (!entries.length) return path;
    return `${path}?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v as string)}`).join('&')}`;
  };
  /** The parent portal on a tab; `child` only when the notification names the child. */
  const parentTab = (tab: string, child?: string | null) => q('/parent', { child, tab });
  const lessons = (sessionId: string | null) =>
    isParent ? '/lessons/family'
      : isStudent ? '/lessons/my'
      : isStaff ? q('/lessons/schedule', { session: sessionId })
      : null;

  switch (n.type) {
    // ── exams, quizzes, assignments ─────────────────────────────────────────
    case NotificationType.ExamPublished:
    case NotificationType.ExamGraded:
      if (isStudent) return q('/student/exams', { exam: id });
      if (isParent) return parentTab('exams');
      if (isStaff) return n.type === NotificationType.ExamGraded ? '/assessments/grading' : q('/teacher-panel', { tab: 'exams' });
      return null;

    case NotificationType.QuizPublished:
    case NotificationType.QuizGraded:
      if (isStudent) return q('/student/quizzes', { quiz: id });
      if (isParent) return parentTab('quizzes');
      if (isStaff) return id ? `/assessments/quiz-results/${id}` : '/quizzes';
      return null;

    case NotificationType.AssignmentPublished:
    case NotificationType.AssignmentDue:
    case NotificationType.AssignmentGraded:
      if (isStudent) return q('/student/assignments', { assignment: id });
      if (isParent) return parentTab('assignments');
      if (isStaff) return id ? `/assessments/assignment/${id}` : q('/teacher-panel', { tab: 'assignments' });
      return null;

    // ── school ─────────────────────────────────────────────────────────────
    case NotificationType.AttendanceAlert:
    case NotificationType.AbsenceRecorded:
      if (isStudent || isParent) return '/school/absences';
      if (isStaff) return n.type === NotificationType.AbsenceRecorded ? '/school/excuses' : q('/teacher-panel', { tab: 'attendance' });
      return null;

    case NotificationType.ExcuseSubmitted:
    case NotificationType.ExcuseReviewed:
      if (isStaff) return '/school/excuses';
      if (isStudent || isParent) return '/school/absences';
      return null;

    case NotificationType.Announcement:
      return '/school/announcements';

    case NotificationType.ReportCardAvailable:
      if (isStudent) return '/gradebook/report-card';
      if (isParent) return parentTab('reportcard', id);
      if (isStaff) return id ? `/gradebook/report-card/${id}` : '/gradebook';
      return null;

    case NotificationType.ProgressSummary:
      if (isParent) return parentTab('summary', id);
      if (isStudent) return '/student/analytics';
      if (isStaff) return '/ai/insights';
      return null;

    // ── courses (LMS) ──────────────────────────────────────────────────────
    case NotificationType.CourseEnrolled:
    case NotificationType.CourseCompleted:
    case NotificationType.CourseAnnouncement:
    case NotificationType.CourseDiscussionReply:
      if (isStudent) return id ? `/courses/learn/${id}` : '/courses/my';
      if (isParent) return parentTab('courses');
      if (isStaff) return id ? q(`/courses/builder/${id}`, { tab: builderTab(n.type) }) : '/courses';
      return null;

    case NotificationType.CourseDiscussionQuestion:
    case NotificationType.EnrollmentRequested:
    case NotificationType.CourseReviewResponse:
      if (isStaff) return id ? q(`/courses/builder/${id}`, { tab: builderTab(n.type) }) : '/courses';
      if (isStudent) {
        if (!id) return '/courses/my';
        return n.type === NotificationType.CourseDiscussionQuestion ? `/courses/learn/${id}` : `/courses/catalog/course/${id}`;
      }
      if (isParent) return parentTab('courses');
      return null;

    case NotificationType.CertificateIssued:
      if (isStudent) return '/courses/certificates';
      if (isParent) return parentTab('courses');
      if (isStaff) return '/courses';
      return null;

    case NotificationType.LearningPathEnrolled:
    case NotificationType.LearningPathCompleted:
    case NotificationType.LearningPathEnrollmentRequested:
      if (isStaff) return id ? `/courses/paths/${id}` : '/courses/paths';
      if (isStudent) return id ? `/courses/catalog/path/${id}` : '/courses/catalog';
      if (isParent) return parentTab('courses');
      return null;

    // ── private lessons (tutoring) ─────────────────────────────────────────
    case NotificationType.LessonScheduled:
    case NotificationType.LessonCancelled:
    case NotificationType.LessonRescheduled:
    case NotificationType.LessonReport:
    case NotificationType.LessonReminder:
      return lessons(id);

    case NotificationType.LessonPayment:
      if (isStaff) return '/academy/payments';
      return lessons(id);

    case NotificationType.BookingRequested:
      return isStaff ? '/academy/bookings' : null;

    case NotificationType.BookingConfirmed:
    case NotificationType.BookingCancelled:
      if (isStaff) return '/academy/bookings';
      return lessons(null);

    case NotificationType.InquiryReceived:
      return isStaff ? '/academy/inquiries' : null;

    // ── messaging ──────────────────────────────────────────────────────────
    case NotificationType.Message:
    case NotificationType.NewMessage:
      return q('/messages', { thread: id });

    // ── accounts / tenants ─────────────────────────────────────────────────
    case NotificationType.AccountPending:
      return isAdmin ? '/settings/user-approval' : null;

    case NotificationType.AccountApproved:
      return isParent ? '/parent' : '/account/dashboard';

    case NotificationType.TenantSignup:
      return r.has('Administrator') ? '/account/tenant-signups' : null;

    case NotificationType.AccountRejected:
    default:
      return null;
  }
}

/** The course-builder tab (`/courses/builder/:id?tab=`) a course notification belongs to. */
function builderTab(type: NotificationType): string | null {
  switch (type) {
    case NotificationType.EnrollmentRequested:
    case NotificationType.CourseEnrolled:
      return 'students';
    case NotificationType.CourseCompleted:
      return 'progress';
    case NotificationType.CourseDiscussionQuestion:
    case NotificationType.CourseDiscussionReply:
      return 'discussions';
    case NotificationType.CourseAnnouncement:
      return 'announcements';
    case NotificationType.CourseReviewResponse:
      return 'reviews';
    default:
      return null;
  }
}
